// @ts-nocheck
// app/index.tsx — Kursi homepage (modern SaaS product)

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useFocusEffect, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import {
  Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import { ArrowLeft, Search, Sparkles, Target, TrendingUp } from 'lucide-react-native';

import {
  theme, ACCENT, ACCENT_DEEP, ACCENT_SOFT,
  FOREST, INK, INK_FAINT, INK_MUTED, INK_SOFT,
  PAPER, PAPER_SOFT,
} from '@/constants/theme';
import {
  trackButtonClick, trackMentorJoinClick, trackPageView, trackSearchCourse, trackTopicClick,
} from '@/utils/analytics';
import { trackMetaPixelPageView } from '@/utils/metaPixel';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import {
  Body, EditorialButton, EditorialFooter, Eyebrow,
  Section, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';

const SANS        = theme.fonts.regular;
const SANS_MED    = theme.fonts.medium;
const SANS_BOLD   = theme.fonts.bold;
const SANS_SEMI   = theme.fonts.semiBold;
const SANS_BLACK  = theme.fonts.serifBlack; // ExtraBold for display
const IS_WEB      = Platform.OS === 'web';

const QUICK_QUERIES = [
  { query: 'שיווק דיגיטלי לעצמאים',   label: 'שיווק דיגיטלי' },
  { query: 'הכנה לפסיכומטרי',          label: 'פסיכומטרי' },
  { query: 'מסחר בשוק ההון למתחילים',  label: 'שוק ההון' },
  { query: 'פייתון לאנליסטים',          label: 'פייתון' },
  { query: 'UX/UI לבוגרים בלי ניסיון', label: 'UX/UI' },
];

const FIELDS = [
  { title: 'תכנות ופיתוח',       desc: 'Python · JavaScript · React · Full-Stack', href: '/courses/programming' },
  { title: 'שיווק דיגיטלי',       desc: 'Google · Meta · SEO · אנליטיקה',           href: '/courses/digital-marketing' },
  { title: 'בינה מלאכותית',       desc: 'ML · NLP · Prompt Engineering',             href: '/courses/ai' },
  { title: 'סייבר ואבטחת מידע',   desc: 'Red · Blue · CompTIA · Cloud Security',     href: '/courses/cyber' },
  { title: 'דאטה ואנליטיקה',       desc: 'SQL · Power BI · Tableau · Data Science',   href: '/courses/data-ai' },
  { title: 'UI/UX ועיצוב מוצר',   desc: 'Figma · Design Systems · מחקר משתמשים',     href: '/courses/ui-ux' },
  { title: 'שוק ההון וקריפטו',    desc: 'מסחר · ניתוח טכני · ניהול סיכונים',         href: '/courses/stock-market' },
  { title: 'פסיכומטרי ומכינות',  desc: 'סימולציות · כמותי · מילולי · אנגלית',       href: '/courses/psychometric' },
];

const STEPS = [
  {
    icon: <Sparkles color={ACCENT} size={22} />,
    title: 'כותבים בעברית רגילה',
    body: 'משפט אחד מספיק. רוצים להוסיף הקשר — רמה, אונליין, תקציב — הדיוק קופץ מיד.',
  },
  {
    icon: <Target color={ACCENT} size={22} />,
    title: 'המערכת מסננת את הרעש',
    body: 'מתוך מאות מכללות ואלפי קורסים אנחנו משאירים לכם רק שלושה. אלה שמתאימים.',
  },
  {
    icon: <TrendingUp color={ACCENT} size={22} />,
    title: 'משווים ויוצאים לדרך',
    body: 'נכנסים לקורס המתאים. אם יש הטבה — משאירים פרטים ומקבלים אותה.',
  },
];

const FOOTER_COLS = [
  {
    title: 'מקטעים',
    items: [
      { label: 'הקטלוג המלא',  href: '/catalog' },
      { label: 'תחומי לימוד',  href: '/fields' },
      { label: 'שאלון התאמה',  href: '/quiz' },
      { label: 'מדריכי בחירה', href: '/guides' },
      { label: 'מאמרים',       href: '/articles' },
    ],
  },
  {
    title: 'המוצר',
    items: [
      { label: 'איך זה עובד',  href: '/how-it-works' },
      { label: 'המתודולוגיה',  href: '/methodology' },
      { label: 'KURSI LEARN',  href: '/learn' },
      { label: 'שאלות נפוצות', href: '/faq' },
      { label: 'אודות',        href: '/about' },
    ],
  },
  {
    title: 'למנטורים',
    items: [
      { label: 'הצטרפות מנטור',      href: '/mentor-signup' },
      { label: 'כניסה למנטורים',     href: '/mentor/login' },
      { label: 'אונליין מול פרונטלי', href: '/compare/online-vs-frontal' },
      { label: 'הצהרת נגישות',       href: '/legal/accessibility' },
    ],
  },
];

export default function WelcomeScreen() {
  const router = useRouter();
  const { isMobile, isDesktop } = useEditorialBreakpoint();
  const [searchText, setSearchText] = useState('');
  const [focusedQuery, setFocusedQuery] = useState<number | null>(null);
  const scrollRef = useRef<ScrollView | null>(null);

  const handleStartChat = useCallback((from?: string) => {
    if (!IS_WEB) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    trackButtonClick(from ? `start_chat_${from}` : 'start_chat', '/');
    router.push('/chat');
  }, [router]);

  const handleSearch = useCallback(() => {
    const msg = (searchText || '').trim();
    if (!msg) return;
    if (!IS_WEB) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    trackSearchCourse(msg);
    trackButtonClick('search_button_hero', '/');
    router.push({ pathname: '/chat', params: { initialMessage: msg, skipWelcome: 'true' } });
  }, [searchText, router]);

  const handleQuickQuery = useCallback(async (query: string, index: number) => {
    if (!IS_WEB) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setFocusedQuery(index);
    await trackTopicClick(query);
    await trackButtonClick('quick_query', '/');
    setTimeout(() => {
      router.push({ pathname: '/chat', params: { initialMessage: query, skipWelcome: 'true' } });
    }, 120);
  }, [router]);

  useEffect(() => {
    trackMetaPixelPageView();
    trackPageView('/');
  }, []);

  useFocusEffect(useCallback(() => { setFocusedQuery(null); }, []));

  useEffect(() => {
    if (!IS_WEB || typeof document === 'undefined') return;
    document.documentElement.style.backgroundColor = PAPER;
    document.body.style.backgroundColor = PAPER;
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: PAPER }}>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>Kursi · מנוע התאמת הקורסים החכם של ישראל</title>
          <meta name="description" content="Kursi — מנוע התאמת הקורסים החכם של ישראל. AI · שיווק דיגיטלי · תכנות · פסיכומטרי · שוק ההון · סייבר. שלוש התאמות שבאמת מתאימות לכם." />
          <link rel="canonical" href="https://kursiaii.com/" />
          <meta property="og:title" content="Kursi · מנוע התאמת הקורסים החכם של ישראל" />
          <meta property="og:description" content="שלוש התאמות מדויקות לכל מי שמחפש לבחור קורס נכון. ללא רעש, ללא הבטחות שווא." />
          <meta property="og:url" content="https://kursiaii.com/" />
        </Head>
      )}

      <ScrollView ref={scrollRef} style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        {/* ─── HERO ─────────────────────────────────────────────────────── */}
        {/* Mesh gradient hero background */}
        {IS_WEB && (
          <View style={s.heroMesh} pointerEvents="none">
            <View style={s.heroOrb1} />
            <View style={s.heroOrb2} />
            <View style={s.heroOrb3} />
          </View>
        )}
        <Section py="xl" px="md" max={1200}>
          <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', alignItems: 'flex-start', gap: 56 } as any}>

            {/* Main content */}
            <View style={{ flex: isDesktop ? 1.4 : 1 }}>
              {/* Badge */}
              <View style={s.badge}>
                <Text style={s.badgeText}>ללא ספונסרים · ללא פרסומות</Text>
              </View>
              <View style={{ height: 20 }} />

              {/* Headline */}
              <Text style={s.heroHeadline}>
                בשוק עמוס בקורסים,{'\n'}
                <Text style={s.heroAccent}>שלוש התאמות</Text>{' '}
                זה כל מה שצריך.
              </Text>

              <View style={{ height: 20 }} />
              <Text style={s.heroSub}>
                Kursi קוראת מאות אלפי שעות קורסים בישראל ומחזירה לכם רק את שלוש ההתאמות שמתאימות למה שכתבתם — בלי ספונסרים, בלי תוכניות מובטחות.
              </Text>

              <View style={{ height: 32 }} />

              {/* Search */}
              <View style={s.searchWrap}>
                <View style={s.searchRow}>
                  <TextInput
                    value={searchText}
                    onChangeText={setSearchText}
                    onSubmitEditing={handleSearch}
                    placeholder="לדוגמה: רוצה להפוך לאנליסט דאטה תוך 8 חודשים"
                    placeholderTextColor={INK_MUTED}
                    style={s.searchInput}
                    returnKeyType="search"
                    accessibilityLabel="חיפוש קורס בשפה חופשית"
                  />
                  <Pressable
                    onPress={handleSearch}
                    style={({ hovered, pressed }: any) => [
                      s.searchBtn,
                      hovered && s.searchBtnHover,
                      pressed && { opacity: 0.9 },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="קבל שלוש התאמות"
                  >
                    <Search color={PAPER_SOFT} size={16} />
                    <Text style={s.searchBtnText}>קבל 3 התאמות</Text>
                  </Pressable>
                </View>
                <View style={{ height: 12 }} />
                <Text style={s.searchHint}>
                  ככל שתכתבו יותר הקשר — רמה, תקציב, אונליין/פרונטלי — ההתאמה תהיה{' '}
                  <Text style={{ color: ACCENT, fontFamily: SANS_BOLD }}>מדויקת יותר</Text>
                </Text>
              </View>

              <View style={{ height: 24 }} />
              <Text style={s.quickLabel}>חיפושים פופולריים:</Text>
              <View style={{ height: 8 }} />
              <View style={s.quickRow}>
                {QUICK_QUERIES.map((q, i) => (
                  <Pressable
                    key={i}
                    onPress={() => handleQuickQuery(q.query, i)}
                    style={({ hovered, pressed }: any) => [
                      s.quickChip,
                      (hovered || focusedQuery === i) && s.quickChipActive,
                      pressed && { opacity: 0.85 },
                    ]}
                    accessibilityRole="button"
                  >
                    <Text style={[s.quickChipText, focusedQuery === i && s.quickChipTextActive]}>
                      {q.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Sidebar */}
            {isDesktop && (
              <View style={{ flex: 1 }}>
                {/* KURSI LEARN promo card */}
                <View style={s.promoCard}>
                  <View style={s.promoCardAccent} />
                  <Text style={s.promoKicker}>חדש · KURSI LEARN</Text>
                  <View style={{ height: 10 }} />
                  <Text style={s.promoTitle}>למדו עם AI שמכיר את החומר שלכם</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.promoBody}>
                    מעלים חומר, מקבלים מסלול AI עם תרגול, תיקון טעויות ומוכנות למבחן.
                  </Text>
                  <View style={{ height: 20 }} />
                  <EditorialButton
                    label="התחילו ללמוד"
                    onPress={() => { trackButtonClick('learn_sidebar', '/'); router.push('/learn'); }}
                    variant="ink"
                    fullWidth
                    trailing={<ArrowLeft size={15} color={PAPER_SOFT} />}
                  />
                </View>

                <View style={{ height: 20 }} />

                {/* Stats */}
                <View style={s.statsRow}>
                  <View style={s.statBox}>
                    <Text style={s.statNum}>03</Text>
                    <Text style={s.statLabel}>התאמות בכל חיפוש</Text>
                  </View>
                  <View style={s.statDivider} />
                  <View style={s.statBox}>
                    <Text style={s.statNum}>12+</Text>
                    <Text style={s.statLabel}>תחומי לימוד</Text>
                  </View>
                </View>
              </View>
            )}
          </View>
        </Section>

        {/* ─── HOW IT WORKS ─────────────────────────────────────────────── */}
        <View style={s.howSection}>
          <Section py="xxl" px="md" max={1200}>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={s.sectionLabel}>איך זה עובד</Text>
              <View style={{ height: 12 }} />
              <Text style={s.sectionHeadline}>שלושה צעדים. בלי דרמה.</Text>
              <View style={{ height: 10 }} />
              <Text style={s.sectionSub}>
                כל שאר הפלטפורמות שולחות אתכם לגלול. אנחנו מצמצמים בשבילכם.
              </Text>
            </View>

            <View style={{ height: 48 }} />
            <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', gap: 24 } as any}>
              {STEPS.map((step, i) => (
                <View key={i} style={[s.stepCard, { flex: 1 }]}>
                  <View style={s.stepIcon}>{step.icon}</View>
                  <View style={{ height: 16 }} />
                  <Text style={s.stepNum}>{String(i + 1).padStart(2, '0')}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.stepTitle}>{step.title}</Text>
                  <View style={{ height: 8 }} />
                  <Text style={s.stepBody}>{step.body}</Text>
                </View>
              ))}
            </View>

            <View style={{ height: 48 }} />
            <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 12 } as any}>
              <EditorialButton label="התחל התאמה עכשיו" onPress={() => handleStartChat('how_section_cta')} variant="primary" size="lg" trailing={<ArrowLeft size={15} color={PAPER_SOFT} />} />
              <EditorialButton label="פתחו את הקטלוג" onPress={() => { trackButtonClick('catalog_how_section', '/'); router.push('/catalog'); }} variant="secondary" size="lg" />
            </View>
          </Section>
        </View>

        {/* ─── FIELDS GRID ───────────────────────────────────────────────── */}
        <View style={s.fieldsSection}>
          <Section py="xxl" px="md" max={1200}>
            <View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 24 } as any}>
              <View style={{ flex: 1, minWidth: 280 }}>
                <Text style={s.sectionLabelLight}>תחומי לימוד</Text>
                <View style={{ height: 12 }} />
                <Text style={s.fieldsSectionHeadline}>שמונה כיוונים. כל אחד שלם.</Text>
                <View style={{ height: 12 }} />
                <Text style={s.fieldsSectionSub}>
                  לכל תחום יש קטלוג מסודר, ללא פרסומות. תלחצו על תחום — תראו את הקורסים שלו.
                </Text>
              </View>
            </View>

            <View style={{ height: 40 }} />
            <View style={s.fieldsGrid}>
              {FIELDS.map((f, i) => (
                <Pressable
                  key={i}
                  onPress={() => router.push(f.href as any)}
                  style={({ hovered, pressed }: any) => [
                    s.fieldCard,
                    hovered && s.fieldCardHover,
                    pressed && { opacity: 0.9 },
                  ]}
                  accessibilityRole="link"
                  accessibilityLabel={f.title}
                >
                  <Text style={s.fieldIndex}>{String(i + 1).padStart(2, '0')}</Text>
                  <View style={{ height: 20 }} />
                  <Text style={s.fieldTitle}>{f.title}</Text>
                  <View style={{ height: 6 }} />
                  <Text style={s.fieldDesc}>{f.desc}</Text>
                  <View style={{ flex: 1 }} />
                  <View style={{ height: 16 }} />
                  <Text style={s.fieldArrow}>←</Text>
                </Pressable>
              ))}
            </View>

            <View style={{ height: 24 }} />
            <View style={{ flexDirection: 'row-reverse' } as any}>
              <Pressable onPress={() => router.push('/fields')}>
                <Text style={s.viewAllLink}>לכל התחומים ←</Text>
              </Pressable>
            </View>
          </Section>
        </View>

        {/* ─── CTA STRIP ─────────────────────────────────────────────────── */}
        <Section py="xl" px="md" max={1200}>
          <View style={s.ctaStrip}>
            <View style={{ flex: 1 }}>
              <Text style={s.ctaKicker}>למנטורים ומכללות</Text>
              <View style={{ height: 8 }} />
              <Text style={s.ctaTitle}>מלמדים? בואו נדבר.</Text>
              <View style={{ height: 8 }} />
              <Text style={s.ctaBody}>
                Kursi עובדת עם מנטורים ומכללות שמעבירים קורסים איכותיים בישראל. אם זה אתם — נשמח להכניס אתכם למערכת ההתאמות שלנו.
              </Text>
            </View>
            <View style={{ marginRight: IS_WEB ? 32 : 0, marginTop: isMobile ? 16 : 0 }}>
              <EditorialButton
                label="הצטרפות מנטור"
                onPress={() => { trackMentorJoinClick('home_cta_strip'); router.push('/mentor-signup'); }}
                variant="accent"
                size="lg"
                trailing={<ArrowLeft size={15} color={PAPER_SOFT} />}
              />
            </View>
          </View>
        </Section>

        <EditorialFooter columns={FOOTER_COLS} onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  // Badge
  badge: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-end',
    backgroundColor: ACCENT_SOFT,
    borderRadius: 99,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: `${ACCENT}30`,
  } as any,
  badgeText: {
    fontFamily: SANS_SEMI,
    fontSize: 12,
    color: ACCENT_DEEP,
    letterSpacing: 0.2,
  },

  // Hero
  heroHeadline: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 72 : 40,
    lineHeight: IS_WEB ? 80 : 48,
    color: INK,
    letterSpacing: IS_WEB ? -2 : -1,
    textAlign: 'right',
  },
  heroAccent: {
    color: ACCENT,
  },
  heroSub: {
    fontFamily: SANS,
    fontSize: IS_WEB ? 19 : 16,
    lineHeight: IS_WEB ? 30 : 26,
    color: INK_SOFT,
    textAlign: 'right',
    maxWidth: 600,
  },

  // Search
  searchWrap: {
    backgroundColor: PAPER_SOFT,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: `${ACCENT}25`,
    padding: 16,
    ...(IS_WEB ? { boxShadow: `0 4px 24px ${ACCENT}12` } as any : {}),
  },
  searchRow: {
    flexDirection: 'row-reverse',
    alignItems: 'stretch',
    gap: 8,
  } as any,
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 12,
    fontFamily: SANS_MED,
    fontSize: 16,
    color: INK,
    backgroundColor: PAPER,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: `${INK}15`,
    textAlign: 'right',
    ...(IS_WEB ? { outlineStyle: 'none' } as any : {}),
  },
  searchBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: ACCENT,
    borderRadius: 10,
    ...(IS_WEB ? { transition: 'all 180ms ease' } as any : {}),
  } as any,
  searchBtnHover: {
    backgroundColor: ACCENT_DEEP,
  },
  searchBtnText: {
    fontFamily: SANS_BOLD,
    fontSize: 14,
    color: PAPER_SOFT,
    letterSpacing: 0.2,
  },
  searchHint: {
    fontFamily: SANS,
    fontSize: 13,
    color: INK_MUTED,
    textAlign: 'right',
  },

  // Quick chips
  quickLabel: {
    fontFamily: SANS_MED,
    fontSize: 12,
    color: INK_MUTED,
    textAlign: 'right',
    letterSpacing: 0.3,
  },
  quickRow: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 8,
  } as any,
  quickChip: {
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 99,
    borderWidth: 1,
    borderColor: `${INK}20`,
    backgroundColor: PAPER_SOFT,
    ...(IS_WEB ? { transition: 'all 160ms ease' } as any : {}),
  },
  quickChipActive: {
    backgroundColor: ACCENT_SOFT,
    borderColor: `${ACCENT}50`,
  },
  quickChipText: {
    fontFamily: SANS_MED,
    fontSize: 13,
    color: INK_SOFT,
  },
  quickChipTextActive: {
    color: ACCENT_DEEP,
  },

  // Sidebar promo card
  promoCard: {
    backgroundColor: INK,
    borderRadius: 16,
    padding: 28,
    overflow: 'hidden',
    position: 'relative',
  } as any,
  promoCardAccent: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 3,
    backgroundColor: ACCENT,
    borderRadius: 3,
  },
  promoKicker: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.2,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  promoTitle: {
    fontFamily: SANS_BLACK,
    fontSize: 22,
    color: PAPER_SOFT,
    lineHeight: 30,
    textAlign: 'right',
    letterSpacing: -0.4,
  },
  promoBody: {
    fontFamily: SANS,
    fontSize: 14,
    color: INK_FAINT,
    lineHeight: 22,
    textAlign: 'right',
  },

  // Stats
  statsRow: {
    flexDirection: 'row-reverse',
    backgroundColor: PAPER_SOFT,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: `${ACCENT}15`,
    overflow: 'hidden',
  } as any,
  statBox: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 12,
  },
  statNum: {
    fontFamily: SANS_BLACK,
    fontSize: 36,
    color: ACCENT,
    letterSpacing: -1,
  },
  statLabel: {
    fontFamily: SANS_MED,
    fontSize: 12,
    color: INK_MUTED,
    textAlign: 'center',
    marginTop: 4,
  },
  statDivider: {
    width: 1,
    backgroundColor: `${ACCENT}15`,
    alignSelf: 'stretch',
  },

  // How section
  howSection: {
    backgroundColor: PAPER_SOFT,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: `${ACCENT}12`,
  },
  sectionLabel: {
    fontFamily: SANS_SEMI,
    fontSize: 12,
    color: ACCENT,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  sectionHeadline: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 44 : 30,
    color: INK,
    letterSpacing: IS_WEB ? -1.2 : -0.6,
    lineHeight: IS_WEB ? 52 : 38,
    textAlign: 'right',
  },
  sectionSub: {
    fontFamily: SANS,
    fontSize: 16,
    color: INK_SOFT,
    lineHeight: 26,
    textAlign: 'right',
    maxWidth: 560,
  },
  stepCard: {
    backgroundColor: PAPER_SOFT,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: `${ACCENT}18`,
    padding: 24,
    ...(IS_WEB ? { transition: 'box-shadow 200ms ease' } as any : {}),
  },
  stepIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: ACCENT_SOFT,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
  },
  stepNum: {
    fontFamily: SANS_BLACK,
    fontSize: 13,
    color: `${ACCENT}60`,
    letterSpacing: 1,
    textAlign: 'right',
  },
  stepTitle: {
    fontFamily: SANS_BOLD,
    fontSize: 18,
    color: INK,
    textAlign: 'right',
    letterSpacing: -0.3,
  },
  stepBody: {
    fontFamily: SANS,
    fontSize: 14,
    color: INK_MUTED,
    lineHeight: 22,
    textAlign: 'right',
  },

  // Fields
  fieldsSection: {
    backgroundColor: INK,
  },
  sectionLabelLight: {
    fontFamily: SANS_SEMI,
    fontSize: 12,
    color: ACCENT,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  fieldsSectionHeadline: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 44 : 30,
    color: PAPER_SOFT,
    letterSpacing: IS_WEB ? -1.2 : -0.6,
    lineHeight: IS_WEB ? 52 : 38,
    textAlign: 'right',
  },
  fieldsSectionSub: {
    fontFamily: SANS,
    fontSize: 16,
    color: INK_FAINT,
    lineHeight: 26,
    textAlign: 'right',
    maxWidth: 560,
  },
  fieldsGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: 12,
  } as any,
  fieldCard: {
    width: IS_WEB ? 'calc(25% - 9px)' : '47%',
    minWidth: 180,
    padding: 20,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    ...(IS_WEB ? { transition: 'all 200ms ease' } as any : {}),
  } as any,
  fieldCardHover: {
    backgroundColor: `${ACCENT}18`,
    borderColor: `${ACCENT}40`,
  },
  fieldIndex: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.2,
    textAlign: 'right',
  },
  fieldTitle: {
    fontFamily: SANS_BOLD,
    fontSize: 18,
    color: PAPER_SOFT,
    letterSpacing: -0.3,
    textAlign: 'right',
  },
  fieldDesc: {
    fontFamily: SANS,
    fontSize: 12,
    color: INK_FAINT,
    lineHeight: 18,
    textAlign: 'right',
  },
  fieldArrow: {
    fontFamily: SANS_BOLD,
    fontSize: 16,
    color: ACCENT,
    textAlign: 'right',
  },
  viewAllLink: {
    fontFamily: SANS_SEMI,
    fontSize: 14,
    color: ACCENT,
    letterSpacing: 0.3,
    ...(IS_WEB ? { textDecorationLine: 'underline' } as any : {}),
  },

  // CTA strip
  ctaStrip: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flexWrap: 'wrap',
    backgroundColor: INK,
    borderRadius: 20,
    padding: 36,
    gap: 24,
    borderWidth: 1,
    borderColor: `${ACCENT}20`,
  } as any,
  ctaKicker: {
    fontFamily: SANS_SEMI,
    fontSize: 11,
    color: ACCENT,
    letterSpacing: 1.4,
    textTransform: 'uppercase' as any,
    textAlign: 'right',
  },
  ctaTitle: {
    fontFamily: SANS_BLACK,
    fontSize: IS_WEB ? 28 : 22,
    color: PAPER_SOFT,
    letterSpacing: -0.5,
    textAlign: 'right',
  },
  ctaBody: {
    fontFamily: SANS,
    fontSize: 15,
    color: INK_FAINT,
    lineHeight: 24,
    textAlign: 'right',
  },

  // Mesh hero background orbs
  heroMesh: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: IS_WEB ? 680 : 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
  } as any,
  heroOrb1: {
    position: 'absolute',
    top: -120,
    right: -80,
    width: 560,
    height: 560,
    borderRadius: 280,
    backgroundColor: `${ACCENT}0A`,
    ...(IS_WEB ? {
      background: `radial-gradient(ellipse at center, ${ACCENT}18 0%, transparent 70%)`,
      filter: 'blur(60px)',
    } as any : {}),
  } as any,
  heroOrb2: {
    position: 'absolute',
    top: 200,
    left: -120,
    width: 400,
    height: 400,
    borderRadius: 200,
    backgroundColor: `${ACCENT_DEEP}06`,
    ...(IS_WEB ? {
      background: `radial-gradient(ellipse at center, ${ACCENT_DEEP}12 0%, transparent 70%)`,
      filter: 'blur(80px)',
    } as any : {}),
  } as any,
  heroOrb3: {
    position: 'absolute',
    top: 80,
    right: '40%',
    width: 300,
    height: 300,
    borderRadius: 150,
    backgroundColor: `${ACCENT_SOFT}80`,
    ...(IS_WEB ? {
      background: `radial-gradient(ellipse at center, ${ACCENT_SOFT}60 0%, transparent 70%)`,
      filter: 'blur(48px)',
    } as any : {}),
  } as any,
});
